/*
Agência Dropi - Ecomm & Dropshipping | Dev @ Dropi - agenciadropi.com.br
Tema Dropi. Se comprado em sites de terceiros sem licença oficial, estará sujeito a processos judiciais e inativação da sua loja.
*/

import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Displays dynamic per-item pricing based on quantity and volume pricing tiers.
 * Updates automatically when quantity changes or cart is updated.
 *
 * @typedef {Object} PriceBreak
 * @property {number} quantity - Minimum quantity for this price tier
 * @property {string} price - Formatted price string (e.g., "$9.50 USD")
 *
 * @typedef {Object} PricePerItemRefs
 * @property {HTMLElement} [pricePerItemText] - The text element displaying the price
 *
 * @extends {Component<PricePerItemRefs>}
 */
class PricePerItemComponent extends Component {
  /** @type {PriceBreak[]} */
  #priceBreaks = [];
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#parsePriceBreaks();
    this.#attachEventListeners();
    this.#updatePriceDisplay();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Parses price breaks from data attributes
   */
  #parsePriceBreaks() {
    const minQuantity = parseInt(this.dataset.minQuantity || '') || 1;
    const { variantPrice, priceBreaks: priceBreaksData } = this.dataset;

    // Start with base price tier
    if (variantPrice) {
      this.#priceBreaks.push({ quantity: minQuantity, price: variantPrice });
    }

    // Parse additional price breaks from JSON array
    if (priceBreaksData) {
      const breaks = JSON.parse(priceBreaksData);
      for (const { quantity, price } of breaks) {
        if (quantity && price) {
          this.#priceBreaks.push({ quantity: parseInt(quantity), price });
        }
      }
    }

    // Sort by quantity descending for efficient lookup
    this.#priceBreaks.sort((a, b) => b.quantity - a.quantity);
  }

  /**
   * Attaches event listeners for quantity and cart updates
   */
  #attachEventListeners() {
    const { signal } = this.#abortController;

    // Listen on document to catch all events (more reliable than form-only)
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
  }

  /**
   * Handles quantity selector updates
   * @param {Event} event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to updates for our variant's quantity selector
    const form = this.closest('product-form-component');
    if (!form || !(event.target instanceof Node) || !form.contains(event.target)) return;

    this.#updatePriceDisplay();
  };

  /**
   * Handles cart updates by refreshing display
   */
  #handleCartUpdate = () => {
    this.#updatePriceDisplay();
  };

  /**
   * Gets the total quantity (cart + current input value)
   * @returns {number}
   */
  #getCurrentQuantity() {
    const form = this.closest('product-form-component');
    const quantityInput = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="quantity"]'));
    if (!quantityInput) return 1;

    // Read the current cart quantity from the data attribute
    const cartQty = parseInt(quantityInput.getAttribute('data-cart-quantity') || '0') || 0;
    // Read the current input value (quantity to add)
    const inputQty = parseInt(quantityInput.value) || 1;

    return cartQty + inputQty;
  }

  /**
   * Updates the price display based on current quantity
   */
  updatePriceDisplay() {
    if (!this.#priceBreaks.length || !this.refs.pricePerItemText) return;

    const quantity = this.#getCurrentQuantity();

    // Price breaks are sorted descending, find first tier that quantity qualifies for
    const priceBreak =
      this.#priceBreaks.find((pb) => quantity >= pb.quantity) ?? this.#priceBreaks[this.#priceBreaks.length - 1];

    if (priceBreak) {
      this.refs.pricePerItemText.innerHTML = `${this.dataset.atText} ${priceBreak.price}/${this.dataset.eachText}`;
    }
  }

  /**
   * Private wrapper for event handlers
   */
  #updatePriceDisplay = () => {
    this.updatePriceDisplay();
  };
}

if (!customElements.get('price-per-item')) {
  customElements.define('price-per-item', PricePerItemComponent);
}


/*
Agência Dropi - Ecomm & Dropshipping | Dev @ Dropi - agenciadropi.com.br
Tema Dropi. Se comprado em sites de terceiros sem licença oficial, estará sujeito a processos judiciais e inativação da sua loja.
*/

(() => {
  "use strict";

  const t = (t, e = document) => e.querySelector(t),
    e = (t, e = document) => Array.from(e.querySelectorAll(t)),
    n = (t) =>
      (t && t.closest && t.closest(".shopify-section, dialog")) || document,
    a = (t, e = {}) => {
      try {
        return JSON.parse(t || "");
      } catch (t) {
        return e;
      }
    },
    r = (t) => {
      const e = parseFloat(String(t ?? "0").replace(",", "."));
      return Number.isFinite(e) ? e : 0;
    },
    o = (t, e) => {
      let n = t || navigator.language || "pt-BR",
        a = e || "BRL";
      /^[A-Z]{2}$/.test(n) &&
        (n = "BR" === n ? "pt-BR" : `${n.toLowerCase()}-${n}`);
      try {
        const t = new Intl.NumberFormat(n, {
          style: "currency",
          currency: a,
        });
        return (e) => t.format((e || 0) / 100);
      } catch (t) {
        return (t) => ((t || 0) / 100).toFixed(2);
      }
    };

  function i(t) {
    const e = (t && t.detail) || {};
    if (e.variant) return e.variant;
    if (e.product && e.product.selectedVariant)
      return e.product.selectedVariant;
    const n = e.data || {};
    return n.variant
      ? n.variant
      : n.newProduct && n.newProduct.selectedVariant
      ? n.newProduct.selectedVariant
      : null;
  }

  function s(t, e) {
    if (!t || "number" != typeof e) return;
    let n = parseInt(t.dataset.insMax || "12", 10),
      i = parseInt(t.dataset.insFree || "0", 10);
    (!Number.isFinite(n) || n < 1) && (n = 12),
      (!Number.isFinite(i) || i < 0) && (i = 0),
      i > n && (i = n);
    const s = a(t.dataset.interestmap, {}),
      d = r(t.dataset.pixPct || "0"),
      c = t.dataset.currency || "BRL",
      u = t.dataset.locale || navigator.language || "pt-BR",
      l = o(u, c),
      m = t.querySelector(".installments-table tbody"),
      p = t.querySelector(".pix-amount"),
      f = t.querySelector(".boleto-amount");

    if (m)
      for (let t = 1; t <= n; t++) {
        const n = m.querySelector(`tr[data-n="${t}"]`);
        if (!n) continue;
        let a = r(s[t]);
        t <= i && (a = 0);
        const o = e + Math.round((e * a) / 100),
          d = Math.round(o / t),
          c = n.querySelector(".parcela"),
          u = n.querySelector(".tot");
        c && (c.textContent = `${t}x ${l(d)} ${t <= i ? "sem juros" : ""}`),
          u && (u.textContent = l(o));
      }

    if (p) {
      const t = Math.round((e * d) / 100);
      p.textContent = `${l(e - t)} à vista`;
    }

    f && (f.textContent = `${l(e)} à vista`);
  }

  function d(t, e) {
    if (!t || "number" != typeof e) return;
    const a = r(t.dataset.discountPct || "0"),
      i = n(t).querySelector("dialog-component.payment-method-modal"),
      s = t.dataset.currency || (i && i.dataset.currency) || "BRL",
      d = t.dataset.locale || (i && i.dataset.locale) || navigator.language || "pt-BR",
      c = o(d, s),
      u = t.querySelector(".pix-price"),
      l = t.querySelector(".pix-save"),
      m = Math.round((e * a) / 100),
      p = e - m;
    u && (u.textContent = c(p)), l && (l.textContent = c(m));
  }

  function c(t, e) {
    if (!t || "number" != typeof e) return;
    const i = n(t);

(function (t) {
  if (!t || t.dataset.dmInlineFrozen) return;

  const originalText = t.textContent || "";
  const text = originalText
    .toLowerCase()
    .normalize("NFD")
    .replace(/\s+/g, " ")
    .trim();

  // Detecta parcelas (ex: 12x)
  const matchParcelas = text.match(/(\d+)\s*x/i);
  const parcelas = matchParcelas ? parseInt(matchParcelas[1], 10) : 12;

  // Detecta valor (R$)
  const matchValor = text.match(/r\$?\s*([\d.,]+)/i);
  const valor = matchValor ? matchValor[1] : "0,00";

  // Detecta "sem juros"
  const semJuros = text.includes("sem juros");

  // Texto padronizado (COM "de")
  t.textContent = `Até ${parcelas}x de R$ ${valor}${semJuros ? " sem juros" : ""}`;

  // Flags mantidas
  t.dataset.dmInlineN = String(parcelas);
  t.dataset.dmInlineHasAte = "1";
  if (semJuros) t.dataset.dmInlineNoInt = "1";
  t.dataset.dmInlineFrozen = "1";
})(t);


    const s = (function (t, e) {
        let n = parseInt(t?.dataset?.insAccepted || "0", 10),
          r = parseInt(t?.dataset?.insFree || "0", 10);
        const o = t?.dataset?.insFormat || "",
          i = e.querySelector("dialog-component.payment-method-modal");
        (!n || n < 1) && i && (n = parseInt(i.dataset.insMax || "12", 10)),
          r < 0 && i && (r = parseInt(i.dataset.insFree || "0", 10)),
          (!Number.isFinite(n) || n < 1) && (n = 1),
          (!Number.isFinite(r) || r < 0) && (r = 0),
          r > n && (r = n);
        return {
          insAccepted: n,
          insFree: r,
          insFormat: o,
          interestMap: a(
            t?.dataset?.interestmap,
            a(i?.dataset?.interestmap, {})
          ),
          locale:
            t?.dataset?.locale || i?.dataset?.locale || navigator.language || "pt-BR",
          currency:
            t?.dataset?.currency || i?.dataset?.currency || "BRL",
        };
      })(t, i),
      d = o(s.locale, s.currency);

    let c = t.dataset.dmInlineN ? parseInt(t.dataset.dmInlineN, 10) : null;
    (!Number.isFinite(c) || c < 1) &&
      (c =
        "max_interest_free" === s.insFormat
          ? s.insFree > 0
            ? s.insFree
            : 1
          : s.insAccepted > 0
          ? s.insAccepted
          : 1);

const u = t.dataset.dmInlineNoInt === "1";
let l = u ? 0 : r(s.interestMap[c]);
const m = e + Math.round((e * l) / 100),
  p = Math.round(m / c),
  f = t.dataset.dmInlineHasAte === "1" ? "Até " : "",
  y = u ? " sem juros" : "";

/* limpa conteúdo */
t.innerHTML = "";

/* SVG */
const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
svg.setAttribute("viewBox", "0 0 32 32");
svg.setAttribute("width", "18");
svg.style.verticalAlign = "middle";

const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
path.setAttribute(
  "d",
  "M 5 6 C 3.355469 6 2 7.355469 2 9 L 2 23 C 2 24.644531 3.355469 26 5 26 L 27 26 C 28.644531 26 30 24.644531 30 23 L 30 9 C 30 7.355469 28.644531 6 27 6 Z M 5 8 L 27 8 C 27.566406 8 28 8.433594 28 9 L 28 11 L 5 11 L 5 13 L 28 13 L 28 23 C 28 23.566406 27.566406 24 27 24 L 5 24 C 4.433594 24 4 23.566406 4 23 L 4 9 C 4 8.433594 4.433594 8 5 8 Z"
);

svg.appendChild(path);

/* garante alinhamento horizontal */
t.style.display = "inline-flex";
t.style.alignItems = "center";

/* monta */
t.appendChild(svg);
t.appendChild(
  document.createTextNode(`${f}${c}x de ${d(p)}${y}`)
);

  }

  function u(t, n) {
    ["#pv_installments_value", ".dm-insline", '[id^="dm-insline-"]']
      .flatMap((n) => e(n, t))
      .forEach((t) => c(t, n));
  }

  function l(t, n) {
    if (!t || !n) return;
    const a =
      "number" == typeof n.price ? n.price : parseInt(String(n.price || 0), 10);
    Number.isFinite(a) &&
      (e(".payment-method-modal", t).forEach((t) => {
        s(t, a);
        const e = t.querySelector('form.payment-terms input[name="id"]');
        e &&
          String(e.value) !== String(n.id) &&
          ((e.value = String(n.id)),
          e.dispatchEvent(new Event("change", { bubbles: !0 })));
      }),
      e('.pix-card[id^="pix-"]', t).forEach((t) => d(t, a)),
      u(t, a));
  }

  function m(n, r) {
    if (!n || !r) return;
    let o = null;
    const i = t(".payment-method-modal", n);
    if (i) {
      const t = a(i.dataset.pricemap, {});
      "number" == typeof t[r] && (o = t[r]);
    }
    if (null == o) {
      const e = t('.pix-card[id^="pix-"]', n);
      if (e) {
        const t = a(e.dataset.variantPrices, {});
        "number" == typeof t[r] && (o = t[r]);
      }
    }
    null != o &&
      (e(".payment-method-modal", n).forEach((t) => {
        s(t, o);
        const e = t.querySelector('form.payment-terms input[name="id"]');
        e &&
          String(e.value) !== String(r) &&
          ((e.value = String(r)),
          e.dispatchEvent(new Event("change", { bubbles: !0 })));
      }),
      e('.pix-card[id^="pix-"]', n).forEach((t) => d(t, o)),
      u(n, o));
  }

  !(function () {
    if (document.documentElement.dataset.dmSyncBound) return;
    document.documentElement.dataset.dmSyncBound = "1";

    document.addEventListener(
      "variant:change",
      (e) => {
        const a = i(e),
          r = n(e.target instanceof Node ? e.target : null);
        if (a) l(r, a);
        else {
          const e = r && t('input[name="id"]', r)?.value;
          e && m(r, e);
        }
      },
      !0
    );

    const a =
      (window.ThemeEvents && window.ThemeEvents.variantUpdate) ||
      "variant:update";
    document.addEventListener(
      a,
      (e) => {
        const a = n(e.target instanceof Node ? e.target : null),
          r = i(e);
        if (r) return void l(a, r);
        const o = e.detail && e.detail.data && e.detail.data.html;
        if (o && o.querySelector) {
          const t = o.querySelector('input[name="id"]')?.value;
          if (t) return void m(a, t);
        }
        const s = t('input[name="id"]', a)?.value;
        s && m(a, s);
      },
      !0
    );

    ["shopify:variant:change", "theme:variant:update", "product:variant-update"].forEach(
      (e) => {
        document.addEventListener(
          e,
          (e) => {
            const a = n(e.target instanceof Node ? e.target : null),
              r = i(e);
            if (r) l(a, r);
            else {
              const e = t('input[name="id"]', a)?.value;
              e && m(a, e);
            }
          },
          !0
        );
      }
    );

    const r = (e) => {
        const n = t('input[name="id"]', e);
        if (!n || n.dataset.dmWatchBound) return;
        n.dataset.dmWatchBound = "1";
        let a = n.value;
        const r = () => {
          n.value !== a && ((a = n.value), m(e, a));
        };
        n.addEventListener("change", r),
          n.addEventListener("input", r),
          setInterval(r, 250);
      },
      o = (n = document) => {
        e(".shopify-section, dialog", n).forEach((e) => {
          t('input[name="id"]', e) && r(e);
        });
      };

    "loading" === document.readyState
      ? document.addEventListener("DOMContentLoaded", () => o())
      : o();

    document.addEventListener("shopify:section:load", (t) =>
      o(t.target || document)
    );
    document.addEventListener("shopify:section:reorder", (t) =>
      o(t.target || document)
    );

    new MutationObserver((e) => {
      for (const n of e)
        n.addedNodes &&
          n.addedNodes.forEach((e) => {
            if (e instanceof HTMLElement)
              if (e.matches(".shopify-section, dialog"))
                t('input[name="id"]', e) && r(e);
              else if (e.querySelector) {
                const n = e.closest?.(".shopify-section, dialog") || e;
                t('input[name="id"]', n) && r(n);
              }
          });
    }).observe(document.documentElement, { childList: !0, subtree: !0 });
  })();
})();
